import React from 'react';
import { Image, Pressable, StyleSheet } from 'react-native';

import { ChevronDown } from '@/assets/icons/ChevronDown';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';

type DesktopSearchResultRowProps = {
    title: string;
    subtitle?: string | null;
    imageUrl?: string;
    onPress: () => void;
    testID?: string;
};

export const DesktopSearchResultRow = ({ title, subtitle, imageUrl, onPress, testID }: DesktopSearchResultRowProps) => {
    const { hovered, hoverHandlers } = useHover();

    return (
        <Pressable
            {...hoverHandlers}
            onPress={onPress}
            style={[styles.row, hovered ? styles.rowHovered : null]}
            accessibilityRole="button"
            testID={testID}
        >
            <Box style={styles.thumbnail}>
                {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" /> : null}
            </Box>
            <Box flex={1}>
                <Text variant="titleMedium" color={ds.colorToken.text.primary} numberOfLines={1}>
                    {title}
                </Text>
                {subtitle ? (
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary} numberOfLines={1} opacity={0.7}>
                        {subtitle}
                    </Text>
                ) : null}
            </Box>
            <Box width={28} alignItems="flex-end" pr="s4">
                <Box width={6.857} height={12} alignItems="center" justifyContent="center">
                    <ChevronDown
                        width={12}
                        height={6.857}
                        viewBox="0 0 14 8"
                        color={hovered ? ds.colorValue.icon.primary : ds.colorValue.icon.secondary}
                        style={styles.chevron}
                    />
                </Box>
            </Box>
        </Pressable>
    );
};

export const DesktopSearchResultRowSkeleton = () => (
    <Box style={styles.row}>
        <LoadingSkeleton
            width={44}
            height={44}
            borderRadius="r12"
            backgroundColor={ds.colorToken.background.neutral.subtle}
        />
        <Box flex={1} gap="s8">
            <LoadingSkeleton
                width={160}
                height={16}
                borderRadius="r8"
                backgroundColor={ds.colorToken.background.neutral.subtle}
            />
            <LoadingSkeleton
                width={220}
                height={12}
                borderRadius="r8"
                backgroundColor={ds.colorToken.background.neutral.subtle}
            />
        </Box>
    </Box>
);

const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 12,
        outlineOffset: -2,
        paddingHorizontal: 16,
        paddingVertical: 8,
    },
    rowHovered: {
        backgroundColor: ds.colorValue.background.transparent.subtle,
    },
    thumbnail: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderRadius: 12,
        height: 44,
        overflow: 'hidden',
        width: 44,
    },
    image: {
        height: '100%',
        width: '100%',
    },
    chevron: {
        transform: [{ rotate: '-90deg' }],
    },
});
